import { mergeToken } from '../services/assetsService';

export const compareStrings = (str1, str2) => str1?.toLowerCase?.() === str2?.toLowerCase?.() || str1 === str2;

export const getValueByMultiProps = (value, possibleKeys = []) => {
  // not all tokens has valid imgs
  const validKey = possibleKeys.find((key) => value?.[key]);
  return value?.[validKey];
};

export const getValueByMultiPropsLowerCased = (value, possibleKeys = []) =>
  getValueByMultiProps(value, possibleKeys)?.toLowerCase();

export const fromFieldsToMockValues = (fields = [], defaultFill = '') =>
  fields.reduce((prev, field) => {
    const prevValue = prev.name ? { [prev.name]: '' || defaultFill } : prev;
    return { ...prevValue, [field.name]: '' || defaultFill };
  });

export const insert = (baseString = '', index, _string = '') => {
  if (index > 0) {
    return baseString.substring(0, index) + _string + baseString.substring(index);
  }

  return _string + baseString;
};

export const divideOnWei = (number) => number / 10 ** 18;

export const objectMapper = (obj, mapSchema) => {
  let mappedObj = {};
  Object.entries(obj).forEach(([key, value]) => {
    const matchedKey = mapSchema?.[key] || key;
    mappedObj = { ...mappedObj, [matchedKey]: value };
  });
  mappedObj = { ...mappedObj, ...obj };
  const keysToDelete = Object.keys(mapSchema);
  keysToDelete.forEach((key) => delete mappedObj?.[key]);
  return mappedObj;
};

export const assetsMapper = (addedTokens = []) =>
  addedTokens.reduce((acc, curr) => {
    if (curr.percentage) {
      acc[curr.token_id || curr.id] = curr.percentage;
    }
    return acc;
  }, {});

// this is an alternative to switch case rendering
// it could also handle the case of multi case key 'STATUS_1 STATUS_2': value
export const multiKeyObject = (key, obj = {}) => {
  const statusKeys = Object.keys(obj);
  let prop = null;
  statusKeys.forEach((_key) => {
    const subKeys = _key.split(' ');
    const foundStatus = subKeys.includes(key);
    if (foundStatus) prop = obj?.[_key];
  });
  return prop;
};

// interface converMapSchema {
//   keys: string[];
//   convert: (value: any) => {};
// }

const simpleConvert = (keys, _obj, converter = () => {}) => {
  let newObj = {};
  keys.forEach((key) => {
    const newValue = converter(_obj?.[key]);
    newObj = { ...newObj, [key]: newValue };
  });
  return { ..._obj, ...newObj };
};

export const constructMiniObj = (keys, baseObj) => {
  let obj = {};
  keys.forEach((key) => {
    obj = { ...obj, [key]: baseObj?.[key] };
  });
  return obj;
};

const isValidValue = (value) => Number.isInteger(value) || !!value;

export const cleanObj = (obj, keysToRemove = []) => {
  let cleanObj = {};
  Object.entries(obj).forEach((entry) => {
    const [key, value] = entry;
    const toRemove = keysToRemove.includes(key);
    const miniObj = isValidValue(value) && !toRemove ? { [key]: value } : {};
    cleanObj = { ...cleanObj, ...miniObj };
  });
  return cleanObj;
};

/**
 * This method is used to create new object with specific keys => removing unwanted keys
 *  inputs:
 *  obj : { name : "TEST", age: 24 }
 *  keepKeys : ["name"]
 *  output :
 *  { name:"TEST" }
 */
export const removeObjKeys = (obj, keepKeys = []) => {
  let newObj = {};
  Object.entries(obj).forEach(([key, value]) => {
    if (!keepKeys.includes(key)) return;
    const miniObj = { [key]: value };
    newObj = { ...newObj, ...miniObj };
  });
  return newObj;
};

export const multiConver = (convertMaps, obj) => {
  let newObj = {};
  // if there's no convert mappings just return it as it is
  if (!convertMaps.length) return obj;
  convertMaps.forEach(({ keys, convert }) => {
    const miniObj = constructMiniObj(keys, obj);
    newObj = { ...obj, ...newObj, ...simpleConvert(keys, miniObj, convert) };
  });
  const afterCleaning = cleanObj(newObj);
  return afterCleaning;
};

export const reArrangeObj = (obj, keysArray) => {
  const keys = keysArray.map((key) => key.name);
  let arrangedObj = {};
  keys.forEach((key) => {
    arrangedObj = { ...arrangedObj, [key]: obj?.[key] };
  });

  return arrangedObj;
};

export const mapAddressToToken = (address = '', tokens = []) =>
  tokens.find((token) => compareStrings(address, token?.token_address));

export const fromArrayToPrimitiveId = (array = [], prop = 'token_id') =>
  array.map((elem = '') => `${elem?.[prop] || ''}`).reduce((curr, prev) => [prev, curr].join(''), '');

export const fromTokensObjToArray = (tokensObj) =>
  Object.values(tokensObj).reduce((acc, curr) => {
    acc[curr.symbol] = mergeToken(curr);
    return acc;
  }, {});

export const formatIsoDate = (dateString = '') => {
  const result = dateString.split(':');
  result.pop();
  return result.join(':');
};

export const captilizeFirstChar = (text = '') => {
  let result = text?.toLowerCase?.();
  result = result
    .split('')
    .map((char, index) => (index ? char : char.toUpperCase()))
    .join('');
  return result;
};

export const getSum = (arr = [], propName = 'value') => arr.reduce((prev, curr) => prev + curr?.[propName], 0);
